import {
  forwardRef,
  type MouseEvent,
  type ReactElement,
  type RefObject,
  useCallback,
  useState,
} from 'react';
import { ReactComponent as WatchTowerIcon } from 'assets/icons/watchTower.svg';

import {
  WATCH_TOWER_PUPIL_REST_X,
  WATCH_TOWER_PUPIL_REST_Y,
  watchTowerPupilOffset,
} from './watchTowerPupilOffset';

import styles from './scss/AgentsManagement.module.scss';

interface IWatchTowerHubIcon {
  pupilX: number,
  pupilY: number,
}

interface IWatchTowerGaze {
  pupil: { x: number, y: number },
  gazeProps: {
    onMouseMove: (event: MouseEvent<HTMLElement>) => void,
    onMouseLeave: () => void,
  },
}

const prefersReducedMotion = (): boolean => (
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

export function useWatchTowerGaze(
  iconRef: RefObject<HTMLSpanElement | null>,
): IWatchTowerGaze {
  const [pupil, setPupil] = useState({
    x: WATCH_TOWER_PUPIL_REST_X,
    y: WATCH_TOWER_PUPIL_REST_Y,
  });

  const onMouseMove = useCallback((event: MouseEvent<HTMLElement>) => {
    if (prefersReducedMotion()) {
      return;
    }
    const icon = iconRef.current;
    if (!icon) {
      return;
    }
    setPupil(watchTowerPupilOffset(
      event.clientX,
      event.clientY,
      icon.getBoundingClientRect(),
      event.currentTarget.getBoundingClientRect(),
    ));
  }, [iconRef]);

  const onMouseLeave = useCallback(() => {
    setPupil({
      x: WATCH_TOWER_PUPIL_REST_X,
      y: WATCH_TOWER_PUPIL_REST_Y,
    });
  }, []);

  return {
    pupil,
    gazeProps: { onMouseMove, onMouseLeave },
  };
}

function WatchTowerEye({ pupilX, pupilY }: IWatchTowerHubIcon): ReactElement {
  return (
    <svg
      className={`${styles.cardIcon} ${styles.watchTowerEye}`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      data-testid="watch-tower-eye"
    >
      <path
        className={`${styles.watchTowerFlame} ${styles.watchTowerFlameA}`}
        d="M12 1.8C14.2 5.2 13.8 8.2 12 10.2C10.2 8.2 9.8 5.2 12 1.8Z"
      />
      <path
        className={`${styles.watchTowerFlame} ${styles.watchTowerFlameB}`}
        d="M12 22.2C14.2 18.8 13.8 15.8 12 13.8C10.2 15.8 9.8 18.8 12 22.2Z"
      />
      <path
        className={`${styles.watchTowerFlame} ${styles.watchTowerFlameC}`}
        d="M1.8 12C5.2 9.8 8.2 10.2 10.2 12C8.2 13.8 5.2 14.2 1.8 12Z"
      />
      <path
        className={`${styles.watchTowerFlame} ${styles.watchTowerFlameD}`}
        d="M22.2 12C18.8 9.8 15.8 10.2 13.8 12C15.8 13.8 18.8 14.2 22.2 12Z"
      />
      <path
        className={`${styles.watchTowerFlame} ${styles.watchTowerFlameE}`}
        d="M4.4 4.4C7.4 5.8 9 8.2 10.4 10.4C7.8 9.2 5.6 7.2 4.4 4.4Z"
      />
      <path
        className={`${styles.watchTowerFlame} ${styles.watchTowerFlameF}`}
        d="M19.6 4.4C16.6 5.8 15 8.2 13.6 10.4C16.2 9.2 18.4 7.2 19.6 4.4Z"
      />
      <path
        className={`${styles.watchTowerFlame} ${styles.watchTowerFlameG}`}
        d="M4.4 19.6C7.4 18.2 9 15.8 10.4 13.6C7.8 14.8 5.6 16.8 4.4 19.6Z"
      />
      <path
        className={`${styles.watchTowerFlame} ${styles.watchTowerFlameH}`}
        d="M19.6 19.6C16.6 18.2 15 15.8 13.6 13.6C16.2 14.8 18.4 16.8 19.6 19.6Z"
      />
      <path
        className={styles.watchTowerAlmond}
        d={
          'M3.6 12C6.4 6.8 9.2 5.2 12 5.2C14.8 5.2 17.6 6.8 20.4 12'
          + 'C17.6 17.2 14.8 18.8 12 18.8C9.2 18.8 6.4 17.2 3.6 12Z'
        }
      />
      <ellipse className={styles.watchTowerIris} cx="12" cy="12" rx="4.35" ry="4.35" />
      <g
        className={styles.watchTowerPupil}
        transform={`translate(${pupilX} ${pupilY})`}
      >
        <ellipse cx="12" cy="12" rx="1.15" ry="3.45" />
        <ellipse className={styles.watchTowerHighlight} cx="12.45" cy="10.5" rx="0.4" ry="0.65" />
      </g>
    </svg>
  );
}

const WatchTowerHubIcon = forwardRef<HTMLSpanElement, IWatchTowerHubIcon>(
  ({ pupilX, pupilY }, ref): ReactElement => (
    <span ref={ref} className={styles.watchTowerIconSlot} aria-hidden="true">
      <WatchTowerIcon
        className={`${styles.cardIcon} ${styles.watchTowerTurret}`}
        data-testid="watch-tower-turret"
      />
      <WatchTowerEye pupilX={pupilX} pupilY={pupilY} />
    </span>
  ),
);

export default WatchTowerHubIcon;
